import { isObject } from './functions';
const STRING = 'string';
const FUNCTION = 'function';
const preventDefault = function () {
    this._defaultPrevented = true;
};
const isDefaultPrevented = function () {
    return this._defaultPrevented === true;
};
/** @hidden */
export class Observable {
    /**
     * Creates a new Observable instance.
     * Initializes the options, events array, and internal event handlers storage.
     */
    constructor() {
        this.options = {};
        this.events = [];
        this._events = {};
    }
    /**
     * Destroys the observable instance and removes all event handlers.
     * This method should be called when the observable is no longer needed to prevent memory leaks.
     */
    destroy() {
        this.unbind();
    }
    /**
     * Binds event handlers to one or more events.
     * @param event The event name(s) to bind to, or an object mapping event names to handlers
     * @param handlers The event handler(s) to bind. Can be a function or an object mapping event names to handlers
     * @param one If true, the handler will be executed only once and then automatically unbound
     * @returns The Observable instance for method chaining
     */
    bind(event, handlers, one) {
        if (!handlers && isObject(event) && !Array.isArray(event)) {
            for (const name in event) {
                if (event[name]) {
                    this.bind(name, event[name]);
                }
            }
            return this;
        }
        const eventNames = typeof event === STRING ? [event] : event, handlersIsFunction = typeof handlers === FUNCTION;
        let original, handler;
        for (let idx = 0, length = eventNames.length; idx < length; idx++) {
            const eventName = eventNames[idx];
            handler = handlersIsFunction ? handlers : handlers[eventName];
            if (handler) {
                if (one) {
                    original = handler;
                    handler = (...args) => {
                        this.unbind(eventName, handler);
                        original.apply(this, args);
                    };
                    handler.original = original;
                }
                this._events[eventName] = this._events[eventName] || [];
                this._events[eventName].push(handler);
            }
        }
        return this;
    }
    /**
     * Binds event handlers that will be executed only once.
     * After execution, the handlers are automatically unbound.
     * @param eventNames The event name(s) to bind to
     * @param handlers The event handler(s) to bind
     * @returns The Observable instance for method chaining
     */
    one(eventNames, handlers) {
        return this.bind(eventNames, handlers, true);
    }
    /**
     * Binds event handlers to be executed first (prepends to the handlers list).
     * These handlers will be called before any previously bound handlers for the same event.
     * @param eventName The event name(s) to bind to
     * @param handlers The event handler(s) to bind
     * @returns The Observable instance for method chaining
     */
    first(eventName, handlers) {
        const eventNames = typeof eventName === STRING ? [String(eventName)] : Array.from(eventName), handlersIsFunction = typeof handlers === FUNCTION;
        let handler;
        for (let idx = 0, length = eventNames.length; idx < length; idx++) {
            const curName = eventNames[idx];
            handler = handlersIsFunction ? handlers : handlers[curName];
            if (handler) {
                this._events[curName] = this._events[curName] || [];
                this._events[curName].unshift(handler);
            }
        }
        return this;
    }
    /**
     * Triggers an event, calling all bound handlers for that event.
     * @param eventName The name of the event to trigger
     * @param eventArgs Optional event arguments to pass to the handlers
     * @returns True if any handler called preventDefault(), false otherwise
     */
    trigger(eventName, eventArgs) {
        let events = this._events[eventName];
        if (events) {
            const e = eventArgs || {};
            e.sender = this;
            e._defaultPrevented = false;
            e.preventDefault = preventDefault;
            e.isDefaultPrevented = isDefaultPrevented;
            events = events.slice();
            for (let idx = 0, length = events.length; idx < length; idx++) {
                events[idx].call(this, e);
            }
            return e._defaultPrevented === true;
        }
        return false;
    }
    /**
     * Removes event handlers from events.
     * @param eventName The event name to unbind from. If undefined, all events are unbound
     * @param handler The specific handler to remove. If undefined, all handlers for the event are removed
     * @returns The Observable instance for method chaining
     */
    unbind(eventName, handler) {
        const events = this._events[eventName];
        if (eventName === undefined) {
            this._events = {};
        }
        else if (events) {
            if (handler) {
                for (let idx = events.length - 1; idx >= 0; idx--) {
                    if (events[idx] === handler || events[idx].original === handler) {
                        events.splice(idx, 1);
                    }
                }
            }
            else {
                this._events[eventName] = [];
            }
        }
        return this;
    }
    /** @hidden */
    _setEvents(options) {
        const length = (this.events || []).length;
        for (let idx = 0; idx < length; idx++) {
            const name = this.events[idx];
            if (this.options[name] && options[name]) {
                this.unbind(name, this.options[name]);
                if (this._events && this._events[name]) {
                    delete this._events[name];
                }
            }
        }
        this.bind(this.events, options);
    }
}
